import React, { useEffect, useMemo, useState } from "react";

interface MarketingSiteProps {
  onSignIn: () => void;
}

interface PageDefinition {
  title: string;
  description: string;
  eyebrow: string;
  headline: string;
  intro: string;
  sections: Array<{ title: string; text: string; points?: string[] }>;
}

const pages: Record<string, PageDefinition> = {
  "/features": {
    title: "Funktionen für dynamische QR-Codes | RedirectQR.de",
    description:
      "Dynamische QR-Codes erstellen, Ziele jederzeit ändern, Scans messen und als PNG oder SVG exportieren.",
    eyebrow: "Produkt",
    headline: "Alles für QR-Codes, die nach dem Druck flexibel bleiben.",
    intro:
      "RedirectQR verbindet zuverlässige Weiterleitungen mit einem fokussierten Dashboard für Teams, Agenturen und lokale Unternehmen.",
    sections: [
      {
        title: "Dynamische Ziele",
        text: "Ändere die Ziel-URL jederzeit. Der bereits gedruckte QR-Code und seine Kurz-URL bleiben unverändert.",
        points: [
          "Permanente QR-ID",
          "Sofortige Zieländerung",
          "Keine Neuproduktion von Drucksachen",
        ],
      },
      {
        title: "Print-ready Export",
        text: "Exportiere jeden Code als hochauflösendes PNG oder skalierbares SVG – wahlweise mit transparentem Hintergrund.",
        points: [
          "PNG und SVG",
          "Transparenter Hintergrund",
          "Logo und Markenfarben",
        ],
      },
      {
        title: "Analytics und Reports",
        text: "Vergleiche Zeiträume und erkenne Geräte, Browser, Länder, neue sowie wiederkehrende Besucher.",
        points: [
          "Tages-, Wochen- und Monatsansicht",
          "CSV- und druckbarer PDF-Export",
          "Top-Codes und Herkunftsdaten",
        ],
      },
      {
        title: "Automatisierte Weiterleitungen",
        text: "Steuere Ziele nach Zeitraum, Wochentag, Uhrzeit, Gerät, Sprache oder Land – immer mit sicherem Standardziel.",
        points: [
          "Zeitpläne und Smart Rules",
          "UTM-Builder und Vorlagen",
          "Fallback-Ziel bei Kampagnenwechseln",
        ],
      },
      {
        title: "Schutz und Health Monitor",
        text: "Begrenze Zugriffe und erkenne fehlerhafte, langsame oder inaktive Ziele frühzeitig.",
        points: [
          "Passwort, Ablauf und Scan-Limit",
          "Einmal- und individuelle Zugangslinks",
          "404-, SSL- und Redirect-Prüfung",
        ],
      },
      {
        title: "Organisation und schneller Workflow",
        text: "Ordner, Tags, Favoriten und Vorlagen halten auch große Kampagnen übersichtlich.",
        points: [
          "Mehrfachauswahl und Bulk-Aktionen",
          "Versionsverlauf mit Wiederherstellung",
          "Interne Notizen und lesbare QR-IDs",
        ],
      },
      {
        title: "Sichere Workspaces",
        text: "Jedes Konto sieht und verwaltet ausschließlich die eigenen QR-Codes. Die Trennung wird in Firestore erzwungen.",
        points: [
          "Owner-basierte Zugriffsregeln",
          "Geschützte Schreibvorgänge",
          "Öffentliche Redirects ohne öffentliches Dashboard",
        ],
      },
    ],
  },
  "/pricing": {
    title: "Preise für dynamische QR-Codes | RedirectQR.de",
    description:
      "Transparent starten: dynamische QR-Codes ohne versteckte Ablaufdaten. RedirectQR Tarife für Einzelpersonen und Unternehmen.",
    eyebrow: "Preise",
    headline: "Einfach starten. Ohne Risiko für gedruckte QR-Codes.",
    intro:
      "Während der Beta ist RedirectQR kostenlos nutzbar. Bestehende QR-Codes laufen weiter – unabhängig davon, wie oft das Ziel geändert wird.",
    sections: [
      {
        title: "Beta – 0 €",
        text: "Für Selbstständige und kleine Unternehmen, die dynamische QR-Codes zuverlässig einsetzen möchten.",
        points: [
          "Dynamische QR-Codes",
          "Scan-Übersicht",
          "PNG- und SVG-Export",
          "Logo und Farben",
        ],
      },
      {
        title: "Pro – in Vorbereitung",
        text: "Für Kampagnen mit höheren Anforderungen an Organisation und Reporting.",
        points: [
          "Ordner und Tags",
          "Erweiterte Analytics",
          "Design-Vorlagen",
          "CSV-Export",
        ],
      },
      {
        title: "Business – in Vorbereitung",
        text: "Für Teams, Agenturen und automatisierte Workflows.",
        points: [
          "Team-Rollen",
          "Bulk-Erstellung",
          "API-Zugang",
          "Health-Warnungen und Reports",
        ],
      },
    ],
  },
  "/solutions/marketing": {
    title: "Dynamische QR-Codes für Marketingkampagnen | RedirectQR.de",
    description:
      "Offline-Kampagnen flexibel steuern und QR-Scans messen – ohne Plakate, Flyer oder Verpackungen neu zu drucken.",
    eyebrow: "Lösung für Marketing",
    headline: "Aus gedruckten Touchpoints werden steuerbare Kampagnen.",
    intro:
      "Verbinde Flyer, Plakate, Verpackungen und Anzeigen mit Zielen, die du jederzeit anpassen kannst.",
    sections: [
      {
        title: "Kampagnen nach dem Druck optimieren",
        text: "Wechsle Landingpages, Angebote oder Formulare, ohne den QR-Code zu verändern.",
        points: [
          "A/B-Ziele manuell wechseln",
          "Saisonale Angebote aktualisieren",
          "Fehlerhafte Links sofort korrigieren",
        ],
      },
      {
        title: "Performance sichtbar machen",
        text: "Erkenne, welche Codes gescannt werden, und priorisiere erfolgreiche Offline-Kanäle.",
        points: [
          "Scan-Zahlen pro Code",
          "Letzte Aktivität",
          "Übersicht für alle Kampagnen",
        ],
      },
    ],
  },
  "/solutions/restaurants": {
    title: "Dynamische QR-Codes für Speisekarten | RedirectQR.de",
    description:
      "Digitale Speisekarten per QR-Code teilen und Ziele jederzeit ändern – ideal für Restaurants, Bars und Hotels.",
    eyebrow: "Lösung für Gastronomie",
    headline: "Eine Speisekarte, die sich ändern darf. Der QR-Code bleibt.",
    intro:
      "Aktualisiere Menüs, Tageskarten und Reservierungslinks, ohne Aufsteller oder Karten neu zu drucken.",
    sections: [
      {
        title: "Menüs aktuell halten",
        text: "Verlinke PDFs oder Webseiten und ändere das Ziel bei jeder neuen Karte.",
        points: [
          "Tages- und Saisonkarten",
          "Mehrsprachige Menüs",
          "Reservierung und Bewertungen",
        ],
      },
      {
        title: "Für den Betrieb gemacht",
        text: "Klare Namen, schnelle Änderungen und druckfähige Dateien machen den täglichen Einsatz einfach.",
        points: [
          "SVG für professionellen Druck",
          "Transparente Exporte",
          "Scan-Übersicht",
        ],
      },
    ],
  },
  "/solutions/real-estate": {
    title: "QR-Codes für Immobilien und Exposés | RedirectQR.de",
    description:
      "Dynamische QR-Codes für Exposés, Schilder und Besichtigungen erstellen und nachträglich aktualisieren.",
    eyebrow: "Lösung für Immobilien",
    headline: "Vom Verkaufsschild direkt zum aktuellen Exposé.",
    intro:
      "Nutze denselben QR-Code für neue Unterlagen, virtuelle Rundgänge oder Terminbuchungen.",
    sections: [
      {
        title: "Objekte flexibel vermarkten",
        text: "Leite Interessenten auf das aktuellste Exposé oder den richtigen Ansprechpartner.",
        points: [
          "Schilder und Schaufenster",
          "Print-Exposés",
          "Besichtigungstermine",
        ],
      },
      {
        title: "Wiederverwendbare Vorlagen",
        text: "Die visuelle Gestaltung bleibt konsistent, während Ziele und Inhalte wechseln.",
        points: ["Logo und Markenfarben", "SVG-Export", "Klare interne Titel"],
      },
    ],
  },
  "/security": {
    title: "Sicherheit und Datenschutz | RedirectQR.de",
    description:
      "So schützt RedirectQR Konten, QR-Code-Daten und Weiterleitungen mit Firebase Authentication und owner-basierten Regeln.",
    eyebrow: "Sicherheit",
    headline: "Deine QR-Codes gehören nur deinem Konto.",
    intro:
      "Mandantentrennung ist bei RedirectQR keine UI-Option, sondern wird direkt in den Datenbankregeln erzwungen.",
    sections: [
      {
        title: "Private Verwaltung",
        text: "Angemeldete Nutzer können ausschließlich QR-Codes lesen, bearbeiten und löschen, deren ownerId ihrer Firebase-UID entspricht.",
        points: [
          "Owner-gefilterte Queries",
          "Firestore Security Rules",
          "Geschützte Logo-Ordner",
        ],
      },
      {
        title: "Zuverlässige Weiterleitungen",
        text: "Die öffentliche Redirect-Funktion liest serverseitig und verändert keine Besitzrechte.",
        points: [
          "Permanente Kurz-URL",
          "Serverseitige Auflösung",
          "Getrennt vom privaten Dashboard",
        ],
      },
      {
        title: "Datensparsame Basis",
        text: "Wir erfassen nur die Daten, die für Verwaltung und grundlegende Scan-Statistiken notwendig sind.",
        points: [
          "Keine öffentliche Code-Liste",
          "Keine Weitergabe von Kontodaten",
          "Löschung durch den Eigentümer",
        ],
      },
    ],
  },
  "/about": {
    title: "Über RedirectQR.de",
    description:
      "RedirectQR.de entwickelt zuverlässige dynamische QR-Codes für Unternehmen, Agenturen und lokale Betriebe.",
    eyebrow: "Über uns",
    headline: "Gebaut für QR-Codes, die auch morgen noch funktionieren.",
    intro:
      "RedirectQR.de ist ein Produkt von Rodriguez Web aus Cuxhaven – entwickelt mit Fokus auf Klarheit, Verlässlichkeit und direkte Betreuung.",
    sections: [
      {
        title: "Unsere Idee",
        text: "Gedruckte QR-Codes dürfen nicht wertlos werden, nur weil sich eine Zielseite ändert. Deshalb trennen wir den permanenten Code vom flexiblen Ziel.",
      },
      {
        title: "Unser Anspruch",
        text: "Ein übersichtliches Werkzeug, transparente Funktionen und keine künstlich komplizierten Workflows.",
        points: [
          "Direkter Support",
          "Entwicklung in Deutschland",
          "Praxisnaher Produktfokus",
        ],
      },
    ],
  },
  "/contact": {
    title: "Kontakt und Support | RedirectQR.de",
    description:
      "Fragen zu RedirectQR, dynamischen QR-Codes oder bestehenden Drucksachen? Kontaktiere uns direkt.",
    eyebrow: "Kontakt",
    headline:
      "Wir helfen, bevor etwas gedruckt wird – und wenn es schon live ist.",
    intro:
      "Schreib uns bei Fragen zu Einrichtung, Migration, Design oder einem bestehenden QR-Code.",
    sections: [
      {
        title: "E-Mail Support",
        text: "Beschreibe kurz deinen Einsatzfall und nenne – falls vorhanden – die QR-ID.",
        points: [
          "Antwort auf Deutsch oder Englisch",
          "Hilfe bei Druckdaten",
          "Migration bestehender Codes",
        ],
      },
      {
        title: "Rodriguez Web",
        text: "Diego Rodriguez · Delftstraße 8 · 27474 Cuxhaven · Telefon: 0155 68942197 · E-Mail: Diego@rodriguez-web.de",
      },
    ],
  },
  "/imprint": {
    title: "Impressum | RedirectQR.de",
    description:
      "Anbieterkennzeichnung und Kontaktinformationen von RedirectQR.de.",
    eyebrow: "Rechtliches",
    headline: "Impressum",
    intro: "Angaben gemäß § 5 DDG.",
    sections: [
      {
        title: "Diensteanbieter",
        text: "Rodriguez Web · Inhaber: Diego Rodriguez · Delftstraße 8 · 27474 Cuxhaven · Deutschland. Telefon: 0155 68942197 · E-Mail: Diego@rodriguez-web.de",
      },
      {
        title: "Verantwortlich für Inhalte",
        text: "Diego Rodriguez · Delftstraße 8 · 27474 Cuxhaven.",
      },
    ],
  },
  "/privacy": {
    title: "Datenschutzerklärung | RedirectQR.de",
    description:
      "Informationen zur Verarbeitung personenbezogener Daten bei RedirectQR.de.",
    eyebrow: "Rechtliches",
    headline: "Datenschutzerklärung",
    intro:
      "Diese Übersicht beschreibt, welche Daten für den Betrieb von RedirectQR verarbeitet werden.",
    sections: [
      {
        title: "Verantwortlicher",
        text: "Diego Rodriguez · Rodriguez Web · Delftstraße 8 · 27474 Cuxhaven · E-Mail: Diego@rodriguez-web.de.",
      },
      {
        title: "Kontodaten",
        text: "Für Anmeldung und Kontoverwaltung verarbeitet Firebase Authentication unter anderem E-Mail-Adresse, UID und technisch erforderliche Anmeldeinformationen.",
      },
      {
        title: "QR- und Scan-Daten",
        text: "Gespeichert werden QR-Konfiguration, Ziel-URL, Gestaltung, Zeitstempel und Scan-Anzahl. Bei einem Scan verarbeiten wir gekürzte technische Merkmale wie Gerätetyp, Browser, Betriebssystem, Sprache, Land und Referrer. Ein pseudonymisierter Hash aus technischen Verbindungsmerkmalen dient ausschließlich der Unterscheidung neuer und wiederkehrender Scans; die IP-Adresse wird nicht als Scan-Datensatz gespeichert. Scan-Ereignisse und inaktive Besucherkennungen werden nach rund 13 Monaten gelöscht.",
      },
      {
        title: "Landingpages und Kontaktanfragen",
        text: "Wenn ein QR-Eigentümer ein Kontaktformular aktiviert, werden nur die im Formular ausgewählten Angaben gespeichert und diesem Eigentümer bereitgestellt. Formularzugriffe werden technisch gegen Missbrauch begrenzt.",
      },
      {
        title: "Benachrichtigungen",
        text: "Health-Warnungen und Wochenberichte werden nur nach Aktivierung im Konto vorbereitet. E-Mail-Versand erfolgt erst nach Verbindung eines Mail-Anbieters und kann jederzeit deaktiviert werden.",
      },
      {
        title: "Hosting und Infrastruktur",
        text: "Die Anwendung nutzt Firebase Authentication, Hosting, Firestore, Storage und Cloud Functions von Google. Dabei können technisch notwendige Protokoll- und Verbindungsdaten anfallen.",
      },
      {
        title: "Deine Rechte",
        text: "Du kannst Auskunft, Berichtigung, Einschränkung der Verarbeitung oder Löschung deiner personenbezogenen Daten verlangen. Kontakt: Diego@rodriguez-web.de.",
      },
    ],
  },
  "/terms": {
    title: "Nutzungsbedingungen | RedirectQR.de",
    description: "Grundlegende Nutzungsbedingungen für RedirectQR.de.",
    eyebrow: "Rechtliches",
    headline: "Nutzungsbedingungen",
    intro: "Klare Regeln für einen zuverlässigen und fairen Betrieb.",
    sections: [
      {
        title: "Zulässige Nutzung",
        text: "QR-Codes dürfen nicht für rechtswidrige, irreführende, schädliche oder missbräuchliche Inhalte eingesetzt werden.",
      },
      {
        title: "Verfügbarkeit",
        text: "Wir entwickeln den Dienst auf hohe Zuverlässigkeit. Eine ununterbrochene Verfügbarkeit kann in der Beta jedoch nicht garantiert werden.",
      },
      {
        title: "Verantwortung für Ziele",
        text: "Nutzer sind für die Inhalte und Rechtmäßigkeit der hinterlegten Ziel-URLs verantwortlich.",
      },
    ],
  },
};

const MarketingSite: React.FC<MarketingSiteProps> = ({ onSignIn }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const path = normalizePath(window.location.pathname);
  const page = pages[path];

  const seo = useMemo(
    () =>
      page || {
        title: "Dynamische QR-Codes erstellen & verwalten | RedirectQR.de",
        description:
          "Erstelle dynamische QR-Codes, ändere Ziele nach dem Druck, analysiere Scans und exportiere PNG oder SVG – auch transparent.",
      },
    [page],
  );

  useEffect(() => {
    document.documentElement.lang = "de";
    document.title = seo.title;
    setMeta("description", seo.description);
    setMeta("robots", "index,follow,max-image-preview:large");
    setPropertyMeta("og:title", seo.title);
    setPropertyMeta("og:description", seo.description);
    setPropertyMeta("og:type", "website");
    setPropertyMeta(
      "og:url",
      `https://redirectqr.de${path === "/" ? "" : path}`,
    );
    setMeta("twitter:card", "summary_large_image");
    setCanonical(`https://redirectqr.de${path === "/" ? "/" : path}`);

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "redirectqr-structured-data";
    const structuredData: object[] = [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "RedirectQR.de",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        url: "https://redirectqr.de/",
        description: seo.description,
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "EUR",
          description: "Kostenlose Beta",
        },
        provider: {
          "@type": "Organization",
          name: "Rodriguez Web",
          email: "Diego@rodriguez-web.de",
          telephone: "+4915568942197",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Delftstraße 8",
            postalCode: "27474",
            addressLocality: "Cuxhaven",
            addressCountry: "DE",
          },
        },
      },
    ];
    if (path === "/")
      structuredData.push({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((item) => ({
          "@type": "Question",
          name: item.question,
          acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      });
    script.text = JSON.stringify(structuredData);
    document.getElementById(script.id)?.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [path, seo]);

  return (
    <div className="marketing-site min-h-screen bg-white text-slate-950">
      <MarketingHeader
        menuOpen={menuOpen}
        setMenuOpen={setMenuOpen}
        onSignIn={onSignIn}
      />
      {path === "/" || !page ? (
        <HomePage onSignIn={onSignIn} />
      ) : (
        <ContentPage page={page} path={path} onSignIn={onSignIn} />
      )}
      <MarketingFooter />
    </div>
  );
};

const MarketingHeader: React.FC<{
  menuOpen: boolean;
  setMenuOpen: (open: boolean) => void;
  onSignIn: () => void;
}> = ({ menuOpen, setMenuOpen, onSignIn }) => (
  <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-xl">
    <div className="mx-auto flex h-[72px] max-w-[1280px] items-center gap-6 px-5 sm:px-8">
      <Brand />
      <nav className="hidden flex-1 items-center gap-1 lg:flex">
        <NavLink href="/features">Funktionen</NavLink>
        <NavLink href="/pricing">Preise</NavLink>
        <div className="group relative">
          <button className="marketing-nav-link">
            Lösungen <span className="text-xs">⌄</span>
          </button>
          <div className="invisible absolute left-0 top-full w-64 translate-y-2 rounded-2xl border border-slate-200 bg-white p-2 opacity-0 shadow-xl transition group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
            <NavLink block href="/solutions/marketing">
              Marketing
            </NavLink>
            <NavLink block href="/solutions/restaurants">
              Gastronomie
            </NavLink>
            <NavLink block href="/solutions/real-estate">
              Immobilien
            </NavLink>
          </div>
        </div>
        <NavLink href="/security">Sicherheit</NavLink>
        <NavLink href="/about">Über uns</NavLink>
      </nav>
      <div className="ml-auto hidden items-center gap-2 sm:flex">
        <button
          type="button"
          onClick={onSignIn}
          className="marketing-secondary-cta"
        >
          Anmelden
        </button>
        <button
          type="button"
          onClick={onSignIn}
          className="marketing-primary-cta"
        >
          Kostenlos starten
        </button>
      </div>
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        className="app-icon-button ml-auto lg:hidden"
        aria-label="Menü öffnen"
      >
        ☰
      </button>
    </div>
    {menuOpen && (
      <nav className="border-t border-slate-200 bg-white p-4 lg:hidden">
        <div className="mx-auto grid max-w-[1280px] gap-1">
          <NavLink block href="/features">
            Funktionen
          </NavLink>
          <NavLink block href="/pricing">
            Preise
          </NavLink>
          <NavLink block href="/solutions/marketing">
            Marketing
          </NavLink>
          <NavLink block href="/solutions/restaurants">
            Gastronomie
          </NavLink>
          <NavLink block href="/solutions/real-estate">
            Immobilien
          </NavLink>
          <NavLink block href="/security">
            Sicherheit
          </NavLink>
          <button
            type="button"
            onClick={onSignIn}
            className="marketing-primary-cta mt-3 justify-center"
          >
            Kostenlos starten
          </button>
        </div>
      </nav>
    )}
  </header>
);

const HomePage: React.FC<{ onSignIn: () => void }> = ({ onSignIn }) => (
  <main>
    <section className="marketing-home-hero">
      <div className="mx-auto grid min-h-[680px] max-w-[1280px] items-center gap-14 px-5 py-16 sm:px-8 lg:grid-cols-[.92fr_1.08fr] lg:py-20">
        <div className="relative z-10">
          <div className="marketing-kicker marketing-kicker-light">
            <span /> Kostenlose Beta · Entwickelt in Deutschland
          </div>
          <h1 className="mt-7 max-w-3xl text-5xl font-semibold leading-[1.01] tracking-[-0.058em] text-slate-950 sm:text-6xl xl:text-[68px]">
            Gedruckt.
            <br /> Flexibel. Messbar.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-slate-600 sm:text-lg">
            Erstelle dynamische QR-Codes, ändere das Ziel jederzeit und erkenne
            genau, wann deine gedruckten Codes genutzt werden – ohne neu zu
            drucken.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onSignIn}
              className="marketing-hero-cta"
            >
              Kostenlos QR-Code erstellen
              <span aria-hidden="true">→</span>
            </button>
            <a
              href="#how-it-works"
              className="rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-400 hover:text-slate-950"
            >
              Produkt ansehen
            </a>
          </div>
          <div className="mt-7 grid max-w-xl gap-3 text-sm text-slate-600 sm:grid-cols-3">
            {[
              ["Keine Kreditkarte", "Kostenlos starten"],
              ["Print-ready", "PNG & SVG"],
              ["Privat", "Nur deine Codes"],
            ].map(([title, text]) => (
              <div key={title} className="flex items-start gap-2">
                <span className="mt-0.5 text-emerald-600">✓</span>
                <span>
                  <strong className="block text-xs text-slate-900">
                    {title}
                  </strong>
                  <small className="text-slate-500">{text}</small>
                </span>
              </div>
            ))}
          </div>
        </div>
        <ProductPreview />
      </div>
      <div className="marketing-hero-grid" aria-hidden="true" />
    </section>

    <section className="border-y border-slate-200 bg-white">
      <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-px bg-slate-200 sm:grid-cols-4">
        {[
          ["Nach dem Druck", "Ziel jederzeit ändern"],
          ["Automatisch", "Health überwachen"],
          ["Tag & Uhrzeit", "Scans verstehen"],
          ["PNG + SVG", "Transparent exportieren"],
        ].map(([value, label]) => (
          <div key={label} className="bg-white px-5 py-7 text-center">
            <p className="text-sm font-bold text-slate-950 sm:text-base">
              {value}
            </p>
            <p className="mt-1 text-xs text-slate-500">{label}</p>
          </div>
        ))}
      </div>
    </section>

    <section className="mx-auto max-w-[1280px] px-5 py-24 sm:px-8">
      <SectionIntro
        eyebrow="Warum RedirectQR"
        title="Dein QR-Code bleibt. Alles dahinter darf sich ändern."
        text="RedirectQR schützt den permanenten Link auf deinen Drucksachen und gibt dir gleichzeitig Kontrolle über Ziel, Zustand und Nutzung."
      />
      <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
        {featureCards.map((item, index) => (
          <FeatureCard key={item.title} index={index + 1} {...item} />
        ))}
      </div>
    </section>

    <section className="border-y border-slate-200 bg-[#f7f8fa] py-24">
      <div className="mx-auto grid max-w-[1280px] gap-12 px-5 sm:px-8 lg:grid-cols-[.8fr_1.2fr] lg:items-center">
        <SectionIntro
          eyebrow="Für gedruckte Codes"
          title="Probleme erkennen, bevor Kunden sie sehen."
          text="Der Health-Monitor prüft Ziel-URL, HTTP-Status, TLS-Zertifikat, Antwortzeit und Redirect-Ketten. Bei Ausfällen kann ein sicheres Notfallziel übernehmen."
        />
        <div className="grid gap-4 sm:grid-cols-2">
          {[
            ["Erreichbarkeit", "404- und Serverfehler früh erkennen", "Aktiv"],
            ["SSL-Zertifikat", "Gültigkeit und Ablauf im Blick", "Gültig"],
            [
              "Geschwindigkeit",
              "Langsame Zielseiten sichtbar machen",
              "248 ms",
            ],
            [
              "Sicheres Fallback",
              "Bei Problemen auf ein Notfallziel leiten",
              "Bereit",
            ],
          ].map(([title, text, status]) => (
            <article
              key={title}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-semibold text-slate-950">{title}</h3>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-700">
                  {status}
                </span>
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>

    <section id="how-it-works" className="bg-slate-950 py-24 text-white">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8">
        <SectionIntro
          dark
          eyebrow="In drei Schritten"
          title="Vom Link zur druckfertigen Datei."
          text="Kein technisches Setup. Erstellen, gestalten, einsetzen."
        />
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {[
            [
              "01",
              "Ziel festlegen",
              "Gib die URL ein und erhalte eine permanente Redirect-Adresse.",
            ],
            [
              "02",
              "Design gestalten",
              "Passe Farben, Logo, Größe und transparenten Hintergrund an.",
            ],
            [
              "03",
              "Drucken & optimieren",
              "Lade PNG oder SVG herunter und ändere das Ziel später jederzeit.",
            ],
          ].map(([number, title, text]) => (
            <div
              key={number}
              className="rounded-2xl border border-white/10 bg-white/[.04] p-6"
            >
              <span className="font-mono text-xs text-blue-300">{number}</span>
              <h3 className="mt-8 text-lg font-semibold">{title}</h3>
              <p className="mt-3 text-sm leading-6 text-slate-400">{text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-[1280px] px-5 py-24 sm:px-8">
      <SectionIntro
        eyebrow="Für den Alltag"
        title="Ein Werkzeug für jeden gedruckten Touchpoint."
        text="Vom Restaurant bis zur Immobilienkampagne – überall dort, wo sich Inhalte schneller ändern als Drucksachen."
      />
      <div className="mt-12 grid gap-5 md:grid-cols-3">
        <SolutionCard
          href="/solutions/marketing"
          title="Marketing"
          text="Flyer, Plakate, Verpackungen und Anzeigen messbar und flexibel machen."
        />
        <SolutionCard
          href="/solutions/restaurants"
          title="Gastronomie"
          text="Menüs, Tageskarten und Reservierungslinks ohne Neudruck aktualisieren."
        />
        <SolutionCard
          href="/solutions/real-estate"
          title="Immobilien"
          text="Exposés, Rundgänge und Termine direkt am Objekt aktuell halten."
        />
      </div>
    </section>

    <section className="border-y border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-[1000px] px-5 py-24 sm:px-8">
        <SectionIntro
          eyebrow="Häufige Fragen"
          title="Wichtig, bevor der Code in den Druck geht."
          text="Kurze Antworten zu Laufzeit, Export und Änderungen."
        />
        <div className="mt-12 grid gap-4">
          {faqs.map((item) => (
            <details
              key={item.question}
              className="group rounded-2xl border border-slate-200 bg-white p-6"
            >
              <summary className="cursor-pointer list-none pr-8 text-base font-semibold text-slate-950">
                {item.question}
                <span className="float-right text-blue-600 transition group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-4 max-w-3xl text-sm leading-7 text-slate-600">
                {item.answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>

    <CTASection onSignIn={onSignIn} />
  </main>
);

const ContentPage: React.FC<{
  page: PageDefinition;
  path: string;
  onSignIn: () => void;
}> = ({ page, path, onSignIn }) => (
  <main>
    <section className="marketing-page-hero">
      <div className="mx-auto max-w-[960px] px-5 py-24 text-center sm:px-8">
        <div className="marketing-kicker mx-auto text-blue-200">
          <span /> {page.eyebrow}
        </div>
        <h1 className="mx-auto mt-7 max-w-4xl text-4xl font-semibold leading-tight tracking-[-.045em] text-white sm:text-6xl">
          {page.headline}
        </h1>
        <p className="mx-auto mt-6 max-w-2xl text-base leading-7 text-slate-400 sm:text-lg">
          {page.intro}
        </p>
        {!["/privacy", "/terms", "/imprint"].includes(path) && (
          <button
            type="button"
            onClick={onSignIn}
            className="mt-8 rounded-xl bg-white px-5 py-3 text-sm font-bold text-slate-950 hover:bg-blue-50"
          >
            Kostenlos starten
          </button>
        )}
      </div>
    </section>
    <section className="mx-auto max-w-[1100px] px-5 py-20 sm:px-8">
      <div className="grid gap-6 md:grid-cols-2">
        {page.sections.map((section) => (
          <article
            key={section.title}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
          >
            <h2 className="text-xl font-semibold tracking-tight text-slate-950">
              {section.title}
            </h2>
            <p className="mt-4 text-sm leading-7 text-slate-600">
              {section.text}
            </p>
            {section.points && (
              <ul className="mt-6 space-y-3">
                {section.points.map((point) => (
                  <li key={point} className="flex gap-3 text-sm text-slate-700">
                    <span className="mt-1 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-emerald-50 text-[11px] font-bold text-emerald-700">
                      ✓
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            )}
            {path === "/contact" && section.title === "E-Mail Support" && (
              <a
                href="mailto:Diego@rodriguez-web.de"
                className="marketing-primary-cta mt-6"
              >
                E-Mail schreiben
              </a>
            )}
          </article>
        ))}
      </div>
    </section>
    {!["/privacy", "/terms", "/imprint", "/contact"].includes(path) && (
      <CTASection onSignIn={onSignIn} />
    )}
  </main>
);

const featureCards = [
  {
    title: "Ziel jederzeit ändern",
    text: "Der gedruckte QR-Code bleibt gleich, auch wenn sich Speisekarte, Kampagne oder Datei ändert.",
  },
  {
    title: "Tage & Uhrzeiten verstehen",
    text: "Erkenne Wochentage, Uhrzeiten, Geräte, Browser und wiederkehrende Scans in deiner Zeitzone.",
  },
  {
    title: "Ziele überwachen",
    text: "Health-Checks erkennen HTTP-Fehler, langsame Seiten, SSL-Probleme und Redirect-Schleifen.",
  },
  {
    title: "Druckfertig exportieren",
    text: "Hochauflösend oder skalierbar – auf Wunsch vollständig transparent.",
  },
  {
    title: "Sicher ändern",
    text: "Jede Zieländerung landet im Versionsverlauf und kann bei Bedarf wiederhergestellt werden.",
  },
  {
    title: "Kampagnen steuern",
    text: "Nutze Zeitpläne, Smart Redirects und UTM-Parameter mit einem sicheren Standardziel.",
  },
];

const faqs = [
  {
    question: "Bleibt mein QR-Code gültig, wenn ich das Ziel ändere?",
    answer:
      "Ja. Die QR-ID und die gedruckte Kurz-URL bleiben gleich. Du änderst nur die dahinterliegende Ziel-URL.",
  },
  {
    question: "Kann ich meinen QR-Code als SVG herunterladen?",
    answer:
      "Ja. Jeder Code kann als skalierbare SVG-Datei oder als hochauflösendes PNG exportiert werden.",
  },
  {
    question: "Ist ein transparenter Hintergrund möglich?",
    answer:
      "Ja. Bei aktiviertem transparenten Hintergrund werden weiße Flächen im QR-Export transparent ausgegeben.",
  },
  {
    question: "Können andere Nutzer meine QR-Codes sehen?",
    answer:
      "Nein. Das Dashboard und die Datenbankregeln erlauben jedem Konto ausschließlich den Zugriff auf die eigenen QR-Codes.",
  },
];

const ProductPreview = () => (
  <div className="relative z-10">
    <div className="marketing-product-preview">
      <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <div className="flex items-center gap-3">
          <QRMark dark />
          <div>
            <p className="text-sm font-bold text-slate-950">Control Center</p>
            <p className="text-[10px] text-slate-400">Beispielansicht</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> System
          online
        </span>
      </div>
      <div className="bg-[#f5f6f8] p-4 sm:p-5">
        <div className="grid grid-cols-3 gap-2.5">
          {[
            ["4", "QR-Codes"],
            ["20", "Scans"],
            ["0", "Warnungen"],
          ].map(([value, label]) => (
            <div
              key={label}
              className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm"
            >
              <p className="text-xl font-semibold text-slate-950">{value}</p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {label}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-950">
                Scan-Aktivität
              </p>
              <p className="mt-1 text-xs text-slate-400">Letzte 7 Tage</p>
            </div>
            <span className="text-xs font-semibold text-blue-600">
              Berlin · UTC+2
            </span>
          </div>
          <div className="mt-5 flex h-24 items-end gap-2">
            {[28, 46, 35, 72, 54, 88, 64].map((height, index) => (
              <div
                key={index}
                className="flex flex-1 flex-col items-center gap-2"
              >
                <span
                  className="w-full rounded-t-md bg-blue-500"
                  style={{ height: `${height}%` }}
                />
                <small className="text-[9px] text-slate-400">
                  {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"][index]}
                </small>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
            ✓
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-950">
              Digitale Speisekarte
            </p>
            <p className="mt-1 truncate text-xs text-slate-400">
              Ziel erreichbar · SSL gültig
            </p>
          </div>
          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase text-emerald-700">
            Aktiv
          </span>
        </div>
      </div>
    </div>
  </div>
);
const FeatureCard: React.FC<{ title: string; text: string; index: number }> = ({
  title,
  text,
  index,
}) => (
  <article className="rounded-2xl border border-slate-200 p-6 transition hover:-translate-y-1 hover:shadow-xl">
    <span className="font-mono text-xs font-semibold text-blue-600">
      0{index}
    </span>
    <h3 className="mt-8 text-lg font-semibold">{title}</h3>
    <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
  </article>
);
const SolutionCard: React.FC<{ href: string; title: string; text: string }> = ({
  href,
  title,
  text,
}) => (
  <a
    href={href}
    className="group rounded-2xl border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:border-blue-200 hover:bg-white hover:shadow-xl"
  >
    <div className="flex items-center justify-between">
      <QRMark />
      <span className="text-slate-400 transition group-hover:translate-x-1 group-hover:text-blue-600">
        →
      </span>
    </div>
    <h3 className="mt-8 text-xl font-semibold">{title}</h3>
    <p className="mt-3 text-sm leading-6 text-slate-500">{text}</p>
  </a>
);
const CTASection: React.FC<{ onSignIn: () => void }> = ({ onSignIn }) => (
  <section className="px-5 py-20 sm:px-8">
    <div className="mx-auto max-w-[1200px] overflow-hidden rounded-[28px] bg-blue-600 px-6 py-16 text-center text-white shadow-2xl shadow-blue-900/20 sm:px-12">
      <p className="text-xs font-semibold uppercase tracking-[.18em] text-blue-100">
        Bereit für den ersten Code?
      </p>
      <h2 className="mx-auto mt-4 max-w-3xl text-3xl font-semibold tracking-[-.04em] sm:text-5xl">
        Erstelle einen QR-Code, der flexibel bleibt.
      </h2>
      <p className="mx-auto mt-5 max-w-xl text-sm leading-6 text-blue-100">
        Kostenlos in der Beta. Keine Kreditkarte erforderlich.
      </p>
      <button
        type="button"
        onClick={onSignIn}
        className="mt-8 rounded-xl bg-white px-5 py-3 text-sm font-bold text-blue-700 hover:bg-blue-50"
      >
        Kostenlos starten
      </button>
    </div>
  </section>
);
const SectionIntro: React.FC<{
  eyebrow: string;
  title: string;
  text: string;
  dark?: boolean;
}> = ({ eyebrow, title, text, dark }) => (
  <div className="max-w-2xl">
    <p
      className={`text-xs font-semibold uppercase tracking-[.18em] ${dark ? "text-blue-300" : "text-blue-600"}`}
    >
      {eyebrow}
    </p>
    <h2
      className={`mt-4 text-3xl font-semibold tracking-[-.04em] sm:text-5xl ${dark ? "text-white" : "text-slate-950"}`}
    >
      {title}
    </h2>
    <p
      className={`mt-5 text-base leading-7 ${dark ? "text-slate-400" : "text-slate-500"}`}
    >
      {text}
    </p>
  </div>
);
const NavLink: React.FC<{
  href: string;
  children: React.ReactNode;
  block?: boolean;
}> = ({ href, children, block }) => (
  <a
    href={href}
    className={`marketing-nav-link ${block ? "flex w-full" : "inline-flex"}`}
  >
    {children}
  </a>
);
const Brand = () => (
  <a href="/" className="flex flex-shrink-0 items-center gap-3">
    <QRMark dark />
    <div>
      <p className="text-sm font-bold tracking-tight">
        RedirectQR<span className="text-blue-600">.de</span>
      </p>
      <p className="text-[9px] font-semibold uppercase tracking-[.18em] text-slate-400">
        Dynamic links
      </p>
    </div>
  </a>
);
const QRMark: React.FC<{ dark?: boolean }> = ({ dark }) => (
  <div
    className={`grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl ${dark ? "bg-slate-950 text-white" : "bg-white text-slate-950"}`}
  >
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM15 14h2v2h-2zM19 14h1v3h-3v3h-3v-2" />
    </svg>
  </div>
);
const MarketingFooter = () => (
  <footer className="border-t border-slate-200 bg-slate-50">
    <div className="mx-auto grid max-w-[1280px] gap-10 px-5 py-14 sm:px-8 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
      <div>
        <Brand />
        <p className="mt-5 max-w-xs text-sm leading-6 text-slate-500">
          Dynamische QR-Codes für Unternehmen, die auch nach dem Druck flexibel
          bleiben wollen.
        </p>
      </div>
      <FooterColumn
        title="Produkt"
        links={[
          ["Funktionen", "/features"],
          ["Preise", "/pricing"],
          ["Sicherheit", "/security"],
        ]}
      />
      <FooterColumn
        title="Unternehmen"
        links={[
          ["Über uns", "/about"],
          ["Kontakt", "/contact"],
        ]}
      />
      <FooterColumn
        title="Rechtliches"
        links={[
          ["Impressum", "/imprint"],
          ["Datenschutz", "/privacy"],
          ["Nutzungsbedingungen", "/terms"],
        ]}
      />
    </div>
    <div className="border-t border-slate-200">
      <div className="mx-auto flex max-w-[1280px] flex-col gap-2 px-5 py-5 text-xs text-slate-400 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <span>© 2026 RedirectQR.de · Rodriguez Web</span>
        <span>Entwickelt in Cuxhaven, Deutschland</span>
      </div>
    </div>
  </footer>
);
const FooterColumn: React.FC<{ title: string; links: string[][] }> = ({
  title,
  links,
}) => (
  <div>
    <p className="text-xs font-bold uppercase tracking-[.14em] text-slate-400">
      {title}
    </p>
    <div className="mt-4 space-y-3">
      {links.map(([label, href]) => (
        <a
          key={href}
          href={href}
          className="block text-sm text-slate-600 hover:text-blue-600"
        >
          {label}
        </a>
      ))}
    </div>
  </div>
);

const normalizePath = (value: string) =>
  value !== "/" && value.endsWith("/") ? value.slice(0, -1) : value;
const setMeta = (name: string, content: string) => {
  let element = document.head.querySelector(
    `meta[name="${name}"]`,
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
};
const setPropertyMeta = (property: string, content: string) => {
  let element = document.head.querySelector(
    `meta[property="${property}"]`,
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  element.content = content;
};
const setCanonical = (href: string) => {
  let element = document.head.querySelector(
    'link[rel="canonical"]',
  ) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = href;
};

export default MarketingSite;
